import { Spinner } from '@iwoca/orion';
import { useParams } from 'react-router';

import { CustomersEmptyState } from './components/CustomersEmptyState/CustomersEmptyState';
import { CustomersTable } from './components/CustomersTable/CustomersTable';
import { CustomersToolbar } from './components/CustomersToolbar/CustomersToolbar';
import { ExportCustomers } from './components/ExportCustomers/ExportCustomers';
import { TableSettingsControl } from './components/TableSettingsDrawer/TableSettingsControl';
import { DEFAULT_TAB_PATH, getTabConfigByPath } from './config/tabConfig';
import {
  useCustomersTableState,
  useStripUnusableParams,
} from './hooks/useCustomersTableState';
import { useIsMobileTableLayout } from './hooks/useIsMobileTableLayout';
import { useSellerHasCustomers } from './hooks/useSellerHasCustomers';
import { TableSettings, useTableSettings } from './hooks/useTableSettings';
import { useTabTotals } from './hooks/useTabTotals';
import { trackPageViewed } from './tracking';
import { useOnce } from '../../hooks/useOnce';
import { SafeNavigate } from '../../routing/utils';

export const CustomersTabView = () => {
  const { tab } = useParams<{ tab: string }>();
  const config = tab ? getTabConfigByPath(tab) : undefined;

  if (!config) {
    return <SafeNavigate to={`/pay/customers/${DEFAULT_TAB_PATH}`} replace />;
  }

  return <CustomersTabViewContent key={config.key} configKey={config.path} />;
};

const CustomersTabViewContent = ({ configKey }: { configKey: string }) => {
  const config = getTabConfigByPath(configKey)!;
  const { settings, setSettings, visibleColumns } = useTableSettings(config);
  const { isMobile } = useIsMobileTableLayout();

  // Each tab is its own route and this component is keyed on the tab, so one
  // mount is one tab view — a tab switch remounts and counts again.
  useOnce({ fn: () => trackPageViewed({ tab: config.path }) });

  const { state, setFilter, setPage } = useCustomersTableState(
    config,
    settings,
  );

  // Owned here, not in the shared state hook: one owner per tab body, so the
  // strip navigation can't race the copies the table and totals would run.
  useStripUnusableParams(config, settings);

  // A settings change resizes the result set, so the current page may not
  // exist in the new one — reset to page 1, mirroring setFilter/setSort.
  const applySettings = (next: TableSettings) => {
    setSettings(next);
    setPage(1);
  };

  // Counts come from a separate query scoped to the tab (view + search), NOT
  // the pill filter — otherwise selecting a pill would zero out the others.
  const { counts } = useTabTotals(config, settings);
  const { hasNoCustomers, loading: loadingHasNoCustomers } =
    useSellerHasCustomers();

  // An empty tab can't tell a brand-new seller from one whose customers have
  // all finished onboarding until the cross-tab count lands, so hold the body
  // back and render it once. Scoped to that one request: the tab totals are
  // keyed on the search term, so waiting on them too would blank the toolbar
  // and table on every search. Keyed on the pending flag rather than a missing
  // count, since a failed request has no count either and must fall through to
  // the table rather than spin forever.
  const resolvingEmptiness = loadingHasNoCustomers;

  // Handed to both the toolbar and the table, but each renders it for exactly
  // one layout: the toolbar only on desktop, the table only inside its mobile
  // branch. Both read the same `isMobile`, so one cog is on screen either way.
  const settingsControl = (
    <TableSettingsControl
      config={config}
      settings={settings}
      onApply={applySettings}
    />
  );

  return (
    <div
      className="flex flex-col gap-xl"
      data-testid={`customers-tab-view-${config.key}`}
    >
      <div className="text-l font-reg text-structure-20">
        {config.description}
      </div>
      {resolvingEmptiness ? (
        <div className="my-2xl flex w-full justify-center">
          <Spinner />
        </div>
      ) : hasNoCustomers ? (
        <CustomersEmptyState />
      ) : (
        <>
          <CustomersToolbar
            config={config}
            activeFilter={state.filterValue}
            counts={counts}
            settings={settings}
            onFilterChange={setFilter}
            rightSlot={
              !isMobile && (
                <div className="flex items-center gap-l">
                  {settingsControl}
                  <ExportCustomers config={config} settings={settings} />
                </div>
              )
            }
          />
          <CustomersTable
            config={config}
            visibleColumns={visibleColumns}
            settings={settings}
            settingsControl={settingsControl}
          />
        </>
      )}
    </div>
  );
};
